import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Switch,
  Card,
  CardContent,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import {
  fetchSecuritySettings,
  updateSecuritySettings,
  fetchRolesWithUsage,
  patchRoleSecurityPolicy,
} from "../../../redux/Slice/Settings/settingsSlice";
import { fetchUserPermissions, fetchUserDetails } from "../../../redux/Slice/User/userDetailsSlice";
import { toggle2FA } from "../../../redux/Slice/Settings/twoFactorSlice";
import AuthView from "../../../utils/AuthView";
import { toast } from "react-hot-toast";
import { checkBeforeOutbound } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";

/** Visible depth on left/right/bottom (parent padding gives room so shadows are not clipped). */
const securitySectionCardSx = (theme) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: "12px",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  border: `1px solid ${theme.palette.divider}`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 1px 2px rgba(0,0,0,0.5), 0 8px 28px rgba(0,0,0,0.4), -8px 0 24px rgba(0,0,0,0.28), 8px 0 24px rgba(0,0,0,0.28)"
      : "0 1px 3px rgba(0,0,0,0.06), 0 8px 28px rgba(0,0,0,0.1), -8px 0 24px rgba(0,0,0,0.07), 8px 0 24px rgba(0,0,0,0.07)",
});

const TwoFactor = () => {
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => state.settings);
  const twoFactorLoading = useSelector((state) => state.twoFactor?.loading || false);
  const securitySettings = useSelector(
    (state) => state.settings.securitySettings
  );
  // Falls back to the value already fetched via fetchUserDetails() until toggle2FA
  // sets an explicit value this session - avoids a second, redundant /api/auth/user call.
  const twoFactorEnabledFromToggle = useSelector((state) => state.twoFactor?.twoFactorEnabled);
  const twoFactorEnabledFromUser = useSelector(
    (state) => state.userDetails.userDetails?.twoFactorEnabled || false
  );
  const twoFactorEnabled = twoFactorEnabledFromToggle ?? twoFactorEnabledFromUser;

  const SecurityPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Security || {}
  );
  const WorkspacePermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Workspace || {}
  );
  const roleName = useSelector(
    (state) => state.userDetails.userDetails?.roleName || null
  );
  const isOwnerRole = roleName === "Owner";
  const hasWorkspaceDelete = WorkspacePermissions.Delete === true;

  // Account Owner may always toggle account-wide team rotation; other roles need Security.Edit + Workspace.Delete
  const canEditTeamAutoPassword =
    isOwnerRole || (SecurityPermissions.Edit === true && hasWorkspaceDelete);
  const canTogglePersonal2FA =
    SecurityPermissions.View === true || isOwnerRole;

  const showSecurityPage =
    isOwnerRole || SecurityPermissions.View === true;

  const canEditRolePolicies =
    SecurityPermissions.Edit === true && hasWorkspaceDelete;

  const rolesWithUsageRaw = useSelector((state) => state.settings.rolesWithUsage);
  const rolesWithUsage = useMemo(() => rolesWithUsageRaw || [], [rolesWithUsageRaw]);

  const nonOwnerRoles = useMemo(
    () => rolesWithUsage.filter((r) => r.roleName !== "Owner"),
    [rolesWithUsage]
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [intervalValue, setIntervalValue] = useState("10");
  const [intervalUnit, setIntervalUnit] = useState("days"); // days | months
  const [isSavingInterval, setIsSavingInterval] = useState(false);
  const [rolePolicyBusyId, setRolePolicyBusyId] = useState(null);
  const [bulkTeamRolePoliciesBusy, setBulkTeamRolePoliciesBusy] = useState(false);

  const busy = loading || twoFactorLoading || bulkTeamRolePoliciesBusy;

  const syncNonOwnerRoleField = useCallback(
    async (field, value) => {
      const targets = rolesWithUsage.filter((r) => r.roleName !== "Owner");
      if (targets.length === 0) return;
      setBulkTeamRolePoliciesBusy(true);
      try {
        await Promise.all(
          targets.map((r) =>
            dispatch(
              patchRoleSecurityPolicy({
                roleId: r._id,
                [field]: value,
              })
            ).unwrap()
          )
        );
      } finally {
        setBulkTeamRolePoliciesBusy(false);
      }
    },
    [dispatch, rolesWithUsage]
  );

  const allNonOwnerTwoFactorOn =
    nonOwnerRoles.length > 0 && nonOwnerRoles.every((r) => r.twoFactorRequired === true);

  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(fetchUserDetails());
    dispatch(fetchSecuritySettings());
  }, [dispatch]);

  useEffect(() => {
    if (showSecurityPage && SecurityPermissions.View) {
      dispatch(fetchRolesWithUsage());
    }
  }, [dispatch, showSecurityPage, SecurityPermissions.View]);

  // Team and Owner share one stored interval (server keeps both fields equal)
  useEffect(() => {
    const intervalDays = Number(
      securitySettings?.autoPasswordChangeIntervalDays ??
        securitySettings?.ownerAutoPasswordChangeIntervalDays ??
        10
    );
    if (intervalDays >= 30 && intervalDays % 30 === 0) {
      setIntervalUnit("months");
      setIntervalValue(String(intervalDays / 30));
    } else {
      setIntervalUnit("days");
      setIntervalValue(String(intervalDays));
    }
  }, [
    securitySettings?.autoPasswordChangeIntervalDays,
    securitySettings?.ownerAutoPasswordChangeIntervalDays,
  ]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleToggle2FA = async () => {
    const next = !twoFactorEnabled;
    try {
      await dispatch(toggle2FA(next)).unwrap();
      setSnackbar({
        open: true,
        message: `2FA ${next ? "enabled" : "disabled"} successfully`,
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: typeof err === "string" ? err : err?.message || "Failed to update 2FA",
        severity: "error",
      });
    }
  };

  const handleToggleAutoPassword = async () => {
    if (!canEditTeamAutoPassword) return;

    const currentEnabled = securitySettings?.autoPasswordChangeEnabled || false;
    const newValue = !currentEnabled;
    const rawValue = Number(intervalValue);
    const computedIntervalDays =
      intervalUnit === "months" ? rawValue * 30 : rawValue;

    if (newValue && (!Number.isInteger(rawValue) || computedIntervalDays < 10)) {
      setSnackbar({
        open: true,
        message: "Please select a valid interval of at least 10 days before enabling",
        severity: "error",
      });
      return;
    }

    try {
      await dispatch(
        updateSecuritySettings({
          autoPasswordChangeEnabled: newValue,
          autoPasswordChangeIntervalDays: computedIntervalDays,
        })
      ).unwrap();
      if (canEditRolePolicies && nonOwnerRoles.length > 0) {
        try {
          await syncNonOwnerRoleField("includeInAutoPasswordRotation", newValue);
        } catch (syncErr) {
          setSnackbar({
            open: true,
            message:
              typeof syncErr === "string"
                ? syncErr
                : syncErr?.message ||
                  "Workspace updated but some roles could not be synced. Adjust the table below if needed.",
            severity: "warning",
          });
          return;
        }
      }
      setSnackbar({
        open: true,
        message: newValue
          ? "Auto password change enabled for team members"
          : "Auto password change disabled",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err || "Failed to update security settings",
        severity: "error",
      });
    }
  };

  const parsedIntervalValue = Number(intervalValue);
  const computedIntervalDays =
    intervalUnit === "months" ? parsedIntervalValue * 30 : parsedIntervalValue;
  const isIntervalValid =
    Number.isInteger(parsedIntervalValue) && parsedIntervalValue > 0 && computedIntervalDays >= 10;
  const canEnableToggle = isIntervalValid;
  const canEnableOwnerToggle = isIntervalValid;

  const canEditSharedInterval = isOwnerRole || canEditTeamAutoPassword;

  const handleSaveSharedInterval = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    const rawValue = Number(intervalValue);
    const computedDays =
      intervalUnit === "months" ? rawValue * 30 : rawValue;

    if (!Number.isInteger(rawValue) || computedDays < 10) {
      setSnackbar({
        open: true,
        message: "Interval must be at least 10 days",
        severity: "error",
      });
      return;
    }

    if (canEditTeamAutoPassword) {
      try {
        setIsSavingInterval(true);
        await dispatch(
          updateSecuritySettings({
            autoPasswordChangeIntervalDays: computedDays,
          })
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Password rotation interval updated",
          severity: "success",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err || "Failed to update interval",
          severity: "error",
        });
      } finally {
        setIsSavingInterval(false);
      }
      return;
    }

    if (isOwnerRole) {
      try {
        setIsSavingInterval(true);
        await dispatch(
          updateSecuritySettings({
            ownerAutoPasswordChangeIntervalDays: computedDays,
          })
        ).unwrap();
        setSnackbar({
          open: true,
          message: "Password rotation interval updated",
          severity: "success",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err || "Failed to update interval",
          severity: "error",
        });
      } finally {
        setIsSavingInterval(false);
      }
    }
  };

  const handleToggleOwnerAutoPassword = async () => {
    if (!isOwnerRole) return;
    const currentEnabled = securitySettings?.ownerAutoPasswordChangeEnabled || false;
    const newValue = !currentEnabled;
    const rawValue = Number(intervalValue);
    const computedDays =
      intervalUnit === "months" ? rawValue * 30 : rawValue;
    if (newValue && (!Number.isInteger(rawValue) || computedDays < 10)) {
      setSnackbar({
        open: true,
        message: "Please select a valid interval of at least 10 days before enabling",
        severity: "error",
      });
      return;
    }
    try {
      await dispatch(
        updateSecuritySettings({
          ownerAutoPasswordChangeEnabled: newValue,
          ownerAutoPasswordChangeIntervalDays: computedDays,
        })
      ).unwrap();
      setSnackbar({
        open: true,
        message: newValue
          ? "Auto password change enabled for your Owner account"
          : "Owner auto password change disabled",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err || "Failed to update owner security settings",
        severity: "error",
      });
    }
  };

  const handleRolePolicyToggle = async (roleId, field, nextValue) => {
    if (!canEditRolePolicies) return;
    setRolePolicyBusyId(roleId);
    try {
      const payload = { roleId };
      if (field === "twoFactorRequired") payload.twoFactorRequired = nextValue;
      else payload.includeInAutoPasswordRotation = nextValue;
      await dispatch(patchRoleSecurityPolicy(payload)).unwrap();
      setSnackbar({
        open: true,
        message: "Role security policy updated",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: typeof err === "string" ? err : err?.message || "Update failed",
        severity: "error",
      });
    } finally {
      setRolePolicyBusyId(null);
    }
  };

  const handleToggleTeamTwoFactorMaster = async () => {
    if (!canEditRolePolicies || nonOwnerRoles.length === 0) return;
    const newValue = !allNonOwnerTwoFactorOn;
    try {
      await syncNonOwnerRoleField("twoFactorRequired", newValue);
      setSnackbar({
        open: true,
        message: newValue
          ? "2FA required for all non-Owner roles"
          : "2FA requirement cleared for all non-Owner roles",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: typeof err === "string" ? err : err?.message || "Failed to update roles",
        severity: "error",
      });
    }
  };

  if (!showSecurityPage) {
    return <AuthView />;
  }

  const teamAutoPasswordReadOnly =
    !canEditTeamAutoPassword && !isOwnerRole && SecurityPermissions.View;
  const showTeamPolicyRow =
    canEditTeamAutoPassword || isOwnerRole || teamAutoPasswordReadOnly;
  const showRolePoliciesCard =
    showSecurityPage &&
    SecurityPermissions.View &&
    (isOwnerRole || rolesWithUsage.length > 0);

  return (
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          minHeight: { xs: 0, sm: 0, md: "fit-content", lg: "fit-content", xl: "fit-content" },
          height: { xs: "100%", sm: "100%", md: "auto", lg: "auto", xl: "auto" },
          flex: { xs: 1, sm: 1, md: "none", lg: "none", xl: "none" },
          borderRadius: "8px",
          backgroundColor: "background.paper",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          overflowY: { xs: "hidden", sm: "hidden", md: "visible", lg: "visible", xl: "visible" },
          color: "text.primary",
          /* Extra horizontal inset so section card shadows have room on left/right */
          px: { xs: 2.5, sm: 3, md: 4, lg: 4.5, xl: 5 },
          pt: { xs: 2, sm: 2.5, md: 3, lg: 3.5, xl: 4 },
          pb: { xs: 3, sm: 3, md: 4, lg: 4.5, xl: 5 },
        }}
      >
        <Box
          sx={{
            flex: { xs: 1, sm: 1, md: "none", lg: "none", xl: "none" },
            minHeight: { xs: 0, sm: 0, md: "auto", lg: "auto", xl: "auto" },
            overflowY: { xs: "auto", sm: "auto", md: "visible", lg: "visible", xl: "visible" },
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: { xs: "16px", sm: "18px", md: "20px", lg: "20px", xl: "20px" },
            "& > .MuiCard-root": {
              flexShrink: 0,
            },
            scrollbarWidth: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
            msOverflowStyle: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
            "&::-webkit-scrollbar": {
              width: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" },
              height: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#555" : "#888",
              borderRadius: "10px",
            },
          }}
        >
        {canTogglePersonal2FA && (
          <Card
            sx={(theme) => ({
              ...securitySectionCardSx(theme),
            })}
        >
          <CardContent
            sx={{
              padding: { xs: "16px", sm: "20px", md: "24px", lg: "24px", xl: "24px" },
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: "10px",
              }}
            >
              <SecurityIcon sx={{ fontSize: 32, color: "primary.main" }} />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  Two-Factor Authentication
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ marginBottom: "20px", color: "text.secondary" }}
            >
                Enable or disable two-factor authentication for <strong>your</strong> login.
                This only affects your account, not other team members.
            </Typography>
            <Divider sx={{ marginBottom: "20px" }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                backgroundColor: "background.default",
                borderRadius: "8px",
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "inset 0 0 0 1px rgba(255,255,255,0.08), 0 1px 4px rgba(0,0,0,0.35)"
                      : "inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 6px rgba(0,0,0,0.06)",
              }}
            >
              <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: "4px" }}>
                    Email OTP on login
                </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {twoFactorEnabled
                      ? "2FA is enabled for your account"
                      : "2FA is disabled for your account"}
                </Typography>
              </Box>
                  <Switch
                  checked={twoFactorEnabled}
                  onChange={handleToggle2FA}
                  disabled={busy}
                    color="primary"
                  />
            </Box>
              {twoFactorEnabled && (
              <Box
                sx={{
                  padding: "16px",
                  backgroundColor: "success.light",
                  borderRadius: "8px",
                  marginTop: "20px",
                }}
              >
                  <Typography variant="body2" sx={{ color: "success.contrastText" }}>
                    <strong>Note:</strong> You will receive a 6-digit OTP by email when you log in.
                    The OTP expires after 5 minutes.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {showSecurityPage && (
          <Card
            sx={(theme) => ({
              ...securitySectionCardSx(theme),
            })}
          >
            <CardContent sx={{ padding: { xs: "16px", sm: "20px", md: "24px" } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Password rotation interval
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                One interval for this account: it applies to your Owner login when Owner rotation is on,
                and to non-Owner roles when team rotation is on. Turning team rotation on or off updates every
                non-Owner role in the table below (when you have permission to edit role policies).
                {canEditSharedInterval
                  ? ""
                  : " You can view the value; only the Owner or a security administrator can change it."}
              </Typography>
              <Divider sx={{ marginBottom: "20px" }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Change after"
                  type="number"
                  value={intervalValue}
                  onChange={(e) => setIntervalValue(e.target.value)}
                  inputProps={{
                    min: intervalUnit === "months" ? 1 : 10,
                    step: 1,
                  }}
                  size="small"
                  sx={{ minWidth: 160 }}
                  disabled={busy || !canEditSharedInterval}
                  error={intervalValue !== "" && !isIntervalValid}
                  helperText={
                    intervalValue !== "" && !isIntervalValid
                      ? "Minimum is 10 days"
                      : "Minimum is 10 days"
                  }
                />
                <TextField
                  select
                  label="Unit"
                  value={intervalUnit}
                  onChange={(e) => setIntervalUnit(e.target.value)}
                  size="small"
                  sx={{ minWidth: 140 }}
                  disabled={busy || !canEditSharedInterval}
                >
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  onClick={handleSaveSharedInterval}
                  disabled={busy || isSavingInterval || !canEditSharedInterval || !isIntervalValid}
                >
                  Save interval
                </Button>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Current:{" "}
                  {securitySettings?.autoPasswordChangeIntervalDays ??
                    securitySettings?.ownerAutoPasswordChangeIntervalDays ??
                    10}{" "}
                  days
                </Typography>
              </Box>
            </CardContent>
          </Card>
            )}

        {showRolePoliciesCard && (
        <Card
            sx={(theme) => ({
              ...securitySectionCardSx(theme),
            })}
          >
            <CardContent sx={{ padding: { xs: "16px", sm: "20px", md: "24px" } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                Role security policies
            </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                The switches below update every non-Owner role in the table. You can still fine-tune
                each role afterward. Email OTP for your own login is in the section above.
            </Typography>

              {showTeamPolicyRow && (
                <>
                  <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                backgroundColor: "background.default",
                borderRadius: "8px",
                      mb: 2,
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "inset 0 0 0 1px rgba(255,255,255,0.08), 0 1px 4px rgba(0,0,0,0.35)"
                          : "inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 6px rgba(0,0,0,0.06)",
              }}
            >
              <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: "4px" }}>
                        Team member auto password rotation (account)
                </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {securitySettings?.autoPasswordChangeEnabled
                          ? "On — non-Owner roles below are included in rotation (when role policies are editable)"
                          : "Off — no automatic team password changes; non-Owner inclusion below is turned off"}
                </Typography>
              </Box>
              <Tooltip
                title={
                        teamAutoPasswordReadOnly
                          ? "View only — you need Security.Edit and workspace privileges, or sign in as the account Owner"
                    : ""
                }
                arrow
              >
                <span>
                  <Switch
                          checked={securitySettings?.autoPasswordChangeEnabled || false}
                          onChange={handleToggleAutoPassword}
                          disabled={
                            busy ||
                            !canEditTeamAutoPassword ||
                            (!(securitySettings?.autoPasswordChangeEnabled) && !canEnableToggle)
                          }
                          color="primary"
                        />
                      </span>
                    </Tooltip>
                  </Box>
                  {nonOwnerRoles.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px",
                        backgroundColor: "background.default",
                        borderRadius: "8px",
                        mb: 2,
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "inset 0 0 0 1px rgba(255,255,255,0.08), 0 1px 4px rgba(0,0,0,0.35)"
                            : "inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 6px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, marginBottom: "4px" }}>
                          Require 2FA for all non-Owner roles (workspace)
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {allNonOwnerTwoFactorOn
                            ? "On — every role below (except Owner) requires 2FA at login"
                            : "Off or mixed — use the switch to require 2FA on all non-Owner roles at once"}
                        </Typography>
                      </Box>
                      <Tooltip
                        title={
                          !canEditRolePolicies
                            ? "View only — you need Security.Edit and workspace delete privilege to change role policies"
                            : ""
                        }
                        arrow
                      >
                        <span>
                          <Switch
                            checked={allNonOwnerTwoFactorOn}
                            onChange={handleToggleTeamTwoFactorMaster}
                            disabled={busy || !canEditRolePolicies || rolePolicyBusyId !== null}
                    color="primary"
                  />
                </span>
              </Tooltip>
            </Box>
                  )}
                </>
              )}

              <Divider sx={{ mb: 2 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Role</TableCell>
                    <TableCell align="center">Require 2FA</TableCell>
                    <TableCell align="center">Password rotation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isOwnerRole && (
                    <TableRow key="owner-row">
                      <TableCell>Owner</TableCell>
                      <TableCell align="center">
                        <Typography variant="caption" color="text.secondary">
                          See Email OTP above
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Your Owner login
                          </Typography>
                          <Switch
                            checked={securitySettings?.ownerAutoPasswordChangeEnabled || false}
                            onChange={handleToggleOwnerAutoPassword}
                            disabled={
                              busy ||
                              (!(securitySettings?.ownerAutoPasswordChangeEnabled) &&
                                !canEnableOwnerToggle)
                            }
                            color="primary"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  {rolesWithUsage
                    .filter((r) => r.roleName !== "Owner")
                    .map((r) => (
                      <TableRow key={r._id}>
                        <TableCell>{r.roleName}</TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={!!r.twoFactorRequired}
                            onChange={(e) =>
                              handleRolePolicyToggle(r._id, "twoFactorRequired", e.target.checked)
                            }
                            disabled={!canEditRolePolicies || rolePolicyBusyId === r._id || busy}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={r.includeInAutoPasswordRotation !== false}
                            onChange={(e) =>
                              handleRolePolicyToggle(
                                r._id,
                                "includeInAutoPasswordRotation",
                                e.target.checked
                              )
                            }
                            disabled={!canEditRolePolicies || rolePolicyBusyId === r._id || busy}
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
          </CardContent>
        </Card>
        )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default TwoFactor;
